import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Building2, BadgeCheck, ExternalLink, Plus, ShieldCheck, Vote, FileText } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Propagandas() {
  const { user } = useAuth();
  const [data, setData] = useState({ advertisers: [], ads: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/propagandas").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#1b5e3f] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#f59e0b] bg-white/10 px-3 py-1.5 rounded-full"><Megaphone className="w-3.5 h-3.5" /> Propagandas</span>
          <h1 className="font-head text-4xl sm:text-5xl font-bold text-white mt-5">Anunciantes & Propagandas</h1>
          <p className="text-white/80 mt-3 max-w-2xl">Conheça as marcas e empresas que fazem parte da ponte Brasil–Portugal e divulgam os seus serviços para toda a comunidade.</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button data-testid="propaganda-create-ad-btn" onClick={() => (window.location.href = user ? "/dashboard?tab=ads" : "/login")} className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706]"><Plus className="w-4 h-4 mr-2" /> Criar anúncio</Button>
            <Button data-testid="propaganda-become-advertiser-btn" onClick={() => (window.location.href = user ? "/dashboard?tab=advertiser" : "/login")} variant="outline" className="rounded-xl border-2 border-white text-white bg-transparent hover:bg-white/10">Seja anunciante oficial</Button>
          </div>
        </div>
        <div className="absolute -right-24 -bottom-24 w-80 h-80 rounded-full bg-[#13422c]" />
      </section>

      {/* Criminal background check banner (Polícia Federal) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <a
          href="https://servicos.pf.gov.br/epol-sinic-publico/"
          target="_blank"
          rel="noreferrer"
          data-testid="background-check-banner"
          className="block rounded-3xl border border-[#1b5e3f]/20 bg-gradient-to-r from-[#e8f0ec] to-white p-6 sm:p-8 hover:shadow-md transition-shadow"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[#1b5e3f] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#d97706]">Segurança & Confiança</span>
              <h2 className="font-head text-2xl font-bold text-[#1c1917] mt-1">Consulte os antecedentes criminais</h2>
              <p className="text-sm text-[#57534e] mt-1 max-w-2xl">Garanta mais segurança nas suas contratações e negociações. Faça a consulta gratuita de antecedentes criminais diretamente no serviço oficial da Polícia Federal do Brasil.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-xl bg-[#1b5e3f] text-white px-6 py-3 font-semibold shrink-0 hover:bg-[#13422c] transition-colors">
              Consultar agora <ExternalLink className="w-4 h-4" />
            </span>
          </div>
        </a>

        {/* Certidão de quitação eleitoral (TSE) */}
        <a
          href="https://www.tse.jus.br/servicos-eleitorais/autoatendimento-eleitoral#/certidoes-eleitor/certidao-quitacao-eleitoral"
          target="_blank"
          rel="noreferrer"
          data-testid="electoral-certificate-banner"
          className="block rounded-3xl border border-[#f59e0b]/30 bg-gradient-to-r from-[#fef9ec] to-white p-6 sm:p-8 mt-5 hover:shadow-md transition-shadow"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[#f59e0b] flex items-center justify-center shrink-0">
              <Vote className="w-8 h-8 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1b5e3f]">Documentação</span>
              <h2 className="font-head text-2xl font-bold text-[#1c1917] mt-1">Certidão de Quitação Eleitoral</h2>
              <p className="text-sm text-[#57534e] mt-1 max-w-2xl">Comprove sua regularidade com a Justiça Eleitoral. Emita gratuitamente a sua certidão de quitação eleitoral no autoatendimento oficial do TSE.</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-xl bg-[#f59e0b] text-white px-6 py-3 font-semibold shrink-0 hover:bg-[#d97706] transition-colors">
              Emitir certidão <ExternalLink className="w-4 h-4" />
            </span>
          </div>
        </a>

        {/* Certidão de contagem de tempo — nacionalidade portuguesa (AIMA) */}
        <div data-testid="aima-nationality-banner" className="rounded-3xl border border-[#1b5e3f]/20 bg-gradient-to-r from-[#e8f0ec] to-white p-6 sm:p-8 mt-5">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[#1b5e3f] flex items-center justify-center shrink-0">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#d97706]">Nacionalidade Portuguesa</span>
              <h2 className="font-head text-2xl font-bold text-[#1c1917] mt-1">Tem mais de 5 anos de residência? Solicite a certidão de tempo</h2>
              <p className="text-sm text-[#57534e] mt-1">Para solicitar a <strong>certidão de contagem de tempo para efeitos de nacionalidade portuguesa</strong> na AIMA, preencha o formulário oficial (<strong>Modelo 7</strong>) e submeta-o junto com a cópia do seu <strong>título de residência</strong> e <strong>passaporte</strong>:</p>
              <ul className="text-sm text-[#57534e] mt-2 space-y-1 list-disc pl-5">
                <li>Por e-mail: <strong>geral@aima.gov.pt</strong> e <strong>Certidoes.Nacionalidade@aima.gov.pt</strong></li>
                <li>Por carta com aviso de recebimento para a AIMA</li>
                <li>Presencialmente numa Loja AIMA</li>
              </ul>
              <a
                href="https://aima.gov.pt/media/pages/documents/ff164bae72-1714485830/modelo-7.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="aima-modelo7-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-[#1b5e3f] text-white px-6 py-3 font-semibold mt-4 hover:bg-[#13422c] transition-colors"
              >
                Baixar Modelo 7 (PDF) <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Official advertisers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-2 mb-6">
          <Building2 className="w-5 h-5 text-[#1b5e3f]" />
          <h2 className="font-head text-2xl font-semibold text-[#1c1917]">Anunciantes Oficiais</h2>
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map((i) => <div key={i} className="h-36 rounded-2xl bg-[#e7e5e4]/40 animate-pulse" />)}</div>
        ) : data.advertisers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#e7e5e4] p-10 text-center text-[#78716c]">Ainda não há anunciantes oficiais. <Link to={user ? "/dashboard?tab=advertiser" : "/login"} className="text-[#1b5e3f] font-semibold underline">Seja o primeiro!</Link></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.advertisers.map((a) => (
              <div key={a.user_id} data-testid={`advertiser-${a.user_id}`} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-6 flex flex-col items-center text-center hover:shadow-md transition-shadow">
                <div className="w-16 h-16 rounded-2xl bg-[#e8f0ec] overflow-hidden flex items-center justify-center">
                  {a.picture ? <img src={a.picture} alt={a.name} className="w-full h-full object-cover" /> : <span className="font-head text-2xl font-bold text-[#1b5e3f]">{(a.name || "?")[0]}</span>}
                </div>
                <p className="font-head font-semibold text-[#1c1917] mt-3">{a.name || "Anunciante"}</p>
                <span className="mt-2 text-xs bg-[#e8f0ec] text-[#1b5e3f] px-2.5 py-1 rounded-full inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Anunciante oficial</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Ads */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-center gap-2 mb-6">
          <Megaphone className="w-5 h-5 text-[#1b5e3f]" />
          <h2 className="font-head text-2xl font-semibold text-[#1c1917]">Propagandas em destaque</h2>
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{[1,2,3].map((i) => <div key={i} className="h-56 rounded-2xl bg-[#e7e5e4]/40 animate-pulse" />)}</div>
        ) : data.ads.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#e7e5e4] p-10 text-center text-[#78716c]">Nenhuma propaganda ativa no momento.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.ads.map((ad) => (
              <a key={ad.id} href={ad.link_url || "#"} target={ad.link_url ? "_blank" : undefined} rel="noreferrer" data-testid={`propaganda-ad-${ad.id}`} className={`block rounded-2xl border overflow-hidden hover:shadow-md transition-all hover:-translate-y-1 ${ad.official ? "border-[#1b5e3f]/30 bg-white ring-1 ring-[#1b5e3f]/10" : "border-[#e7e5e4] bg-white"}`}>
                {ad.image_url ? (
                  <img src={ad.image_url} alt={ad.title} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-[#e8f0ec] flex items-center justify-center"><Megaphone className="w-8 h-8 text-[#1b5e3f]/40" /></div>
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase font-bold text-[#d97706]">Anúncio</span>
                    {ad.official && <span className="text-[10px] uppercase font-bold text-[#1b5e3f] bg-[#e8f0ec] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Oficial</span>}
                  </div>
                  <h3 className="font-head font-semibold text-[#1c1917] mt-1">{ad.title}</h3>
                  <p className="text-sm text-[#78716c] line-clamp-2 mt-1">{ad.description}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#e7e5e4]">
                    <span className="text-xs text-[#78716c]">por {ad.owner_name}</span>
                    {ad.link_url && <span className="text-xs font-semibold text-[#1b5e3f] inline-flex items-center gap-1">Visitar <ExternalLink className="w-3 h-3" /></span>}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
